// apps/web/src/lib/kanban/tablet.ts — Fase 11, Plano 15 (KAN-06, KAN-07, D-17, D-20, D-24, D-36).
// Regras puras do tablet do operador (sem React, sem rede).
import type { EstadoCartaoKanban } from '@pieltec/contracts';
import type { NovoEventoOutbox } from '../offline/outbox';

export type AcaoCartaoTablet = 'iniciar' | 'pausar' | 'concluir';

export const TAMANHO_MINIMO_MOTIVO_OUTRO = 2;

/**
 * Ações de um toque disponíveis no cartão.
 * Bloqueado não inicia; se já estava em produção continua podendo concluir (D-24).
 */
export function acoesDoCartao(cartao: {
  estado: EstadoCartaoKanban;
  bloqueio?: { motivo: string } | null;
}): AcaoCartaoTablet[] {
  const bloqueado = Boolean(cartao.bloqueio);
  switch (cartao.estado) {
    case 'AGUARDANDO':
    case 'RETRABALHO':
      // D-08 (Fase 19): o operador pode concluir direto do cartão Aguardando, sem iniciar o relógio.
      return bloqueado ? [] : ['iniciar', 'concluir'];
    case 'EM_PRODUCAO':
      return bloqueado ? ['concluir'] : ['pausar', 'concluir'];
    case 'PAUSADO':
      return bloqueado ? ['concluir'] : ['iniciar', 'concluir'];
    default:
      return [];
  }
}

/** Contador do processo no cabeçalho da célula; a raia "A alocar" nunca aparece no tablet (D-36). */
export function rotuloAguardandoAlocacao(quantidade: number): string | null {
  if (!quantidade || quantidade <= 0) return null;
  return `${quantidade} aguardando alocação`;
}

/** Pausa exige um tipo da lista OU "Outro" com texto (D-20). */
export function validarPausa(input: { tipoInterrupcaoId?: string; outro?: string }): string | null {
  if (input.tipoInterrupcaoId) return null;
  if (input.outro !== undefined) {
    return input.outro.trim().length >= TAMANHO_MINIMO_MOTIVO_OUTRO ? null : 'Descreva o motivo (obrigatório)';
  }
  return 'Selecione um motivo';
}

export interface ValoresConclusao {
  quantidadeBoa: number;
  quantidadeNaoConforme: number;
  motivoNc: string;
}

/**
 * Apontamento pré-preenchido (D-17 da Fase 11, D-10/D-12 da Fase 19): boa = o que chegou da etapa anterior
 * (limitado ao pendente) e, quando nada chegou, o pendente; NC = 0.
 */
export function valoresIniciaisConclusao(cartao: {
  quantidadePendente: number;
  quantidadeDisponivel?: number;
}): ValoresConclusao {
  const pendente = Math.max(0, cartao.quantidadePendente);
  const disponivel = cartao.quantidadeDisponivel ?? 0;
  const boa = disponivel > 0 ? Math.min(disponivel, pendente) : pendente;
  return { quantidadeBoa: boa, quantidadeNaoConforme: 0, motivoNc: '' };
}

export type ModoConcluir = 'concluir' | 'registrar';

export function rotuloBotaoConcluir(
  valores: { quantidadeNaoConforme: number },
  modo: ModoConcluir = 'concluir',
): string {
  if (modo === 'registrar') {
    return valores.quantidadeNaoConforme > 0 ? 'Registrar não conformidade e avançar' : 'Registrar e avançar';
  }
  return valores.quantidadeNaoConforme > 0 ? 'Registrar não conformidade e concluir' : 'Concluir apontamento';
}

/** Rótulo do botão da lista de etapas anteriores (D-14). */
export function rotuloEtapasAnteriores(quantidade: number): string {
  return `Etapas anteriores (${quantidade})`;
}

function normalizarBusca(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

/** Busca local por OP, lote ou item na lista de etapas anteriores (D-13); termo vazio devolve tudo. */
export function filtrarEtapasAnteriores<T extends { opNumero: string; loteCodigo: string; itemNome: string }>(
  itens: readonly T[],
  termo: string,
): T[] {
  const alvo = normalizarBusca(termo);
  if (alvo === '') return [...itens];
  return itens.filter((i) =>
    [i.opNumero, i.loteCodigo, i.itemNome].some((campo) => normalizarBusca(campo).includes(alvo)),
  );
}

/** Texto das etapas que serão lançadas como registro tardio (D-13). */
export function descreverEtapasPuladas(etapas: readonly { etapaOrdem: number; servicoNome: string }[]): string {
  return etapas.map((e) => `Etapa ${e.etapaOrdem} · ${e.servicoNome}`).join(', ');
}

export function validarConclusao(input: { boa: number; nc: number; motivo?: string }): string | null {
  const inteiroValido = (n: number): boolean => Number.isInteger(n) && n >= 0;
  if (!inteiroValido(input.boa) || !inteiroValido(input.nc)) return 'Quantidade inválida.';
  if (input.boa + input.nc <= 0) return 'Informe ao menos uma peça (boa ou não conforme).';
  if (input.nc > 0 && (input.motivo ?? '').trim().length === 0) return 'Informe o motivo da não conformidade.';
  return null;
}

/**
 * Chave de idempotência da conclusão de um cartão (CR-WEB-02). É estável por
 * (cartão, conclusão pendente): reaproveita a chave já emitida em memória ou a de um
 * evento ainda pendente no outbox, e só gera uma nova quando não há conclusão em aberto.
 */
export function obterChaveConclusao(
  emMemoria: Map<string, string>,
  osId: string,
  pendentesNoOutbox: ReadonlyMap<string, string>,
  gerar: () => string = () => crypto.randomUUID(),
): string {
  const existente = emMemoria.get(osId) ?? pendentesNoOutbox.get(osId);
  const chave = existente ?? gerar();
  emMemoria.set(osId, chave);
  return chave;
}

/** Libera as chaves cujos eventos já foram resolvidos (sincronizados ou recusados) pelo servidor. */
export function liberarChavesFinalizadas(emMemoria: Map<string, string>, chavesFinalizadas: readonly string[]): void {
  if (chavesFinalizadas.length === 0) return;
  const finalizadas = new Set(chavesFinalizadas);
  for (const [osId, chave] of emMemoria) {
    if (finalizadas.has(chave)) emMemoria.delete(osId);
  }
}

/**
 * A resposta encerra a tentativa (a chave pode ser descartada)? Sucesso e recusa 4xx sim;
 * 5xx, 408, 429 e 401 deixam o resultado incerto e a chave precisa ser reaproveitada.
 */
export function respostaEncerraConclusao(status: number): boolean {
  if (status >= 200 && status < 300) return true;
  return status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429;
}

/**
 * Converte o texto digitado em quantidade: só dígitos (sem "1e3", "0x10", sinal ou
 * decimais). Vazio vale 0; texto inválido devolve NaN, que a validação recusa.
 */
export function converterQuantidade(texto: string): number {
  const limpo = texto.trim();
  if (limpo === '') return 0;
  if (!/^[0-9]+$/.test(limpo)) return Number.NaN;
  const n = Number(limpo);
  return Number.isSafeInteger(n) ? n : Number.NaN;
}

/** Total apontado acima do pendente da OS: pede confirmação explícita antes de gravar (WR-WEB-07). */
export function excedePendente(input: { boa: number; nc: number }, quantidadePendente: number): boolean {
  return input.boa + input.nc > Math.max(0, quantidadePendente);
}

export interface ValoresEventoOffline {
  quantidadeBoa: number;
  quantidadeNaoConforme: number;
  motivoNc: string;
  identificadorIdempotente: string;
  actorUserId?: string | null;
  /** Fase 19 (D-17): célula da lista "Etapas anteriores"; campo aditivo do payload (ADR-006). */
  celulaCodigo?: string | null;
}

/**
 * Evento de Apontamento para a fila offline existente (ADR-006). A chave de
 * idempotência é a mesma do formulário, então reenvios não duplicam (T-11-57).
 */
export function montarEventoOffline(
  osId: string,
  valores: ValoresEventoOffline,
  deviceId: string,
  agora: Date = new Date(),
): NovoEventoOutbox {
  const motivo = valores.motivoNc.trim();
  return {
    idempotencyId: valores.identificadorIdempotente,
    aggregateType: 'Apontamento',
    aggregateId: osId,
    deviceId,
    // deviceSequence: alocada pelo outbox (contador monotônico persistido por dispositivo, CR-WEB-01).
    schemaVersion: 1,
    baseVersion: 0,
    clientTime: agora.toISOString(),
    payload: {
      osId,
      quantidadeBoa: valores.quantidadeBoa,
      quantidadeNaoConforme: valores.quantidadeNaoConforme,
      motivoNc: motivo.length > 0 ? motivo : null,
      identificadorIdempotente: valores.identificadorIdempotente,
      // Fase 13 (R13-11): instante e offset do relógio do dispositivo; o servidor valida e decide o dia operacional (SP).
      ocorridoEmDispositivo: agora.toISOString(),
      offsetDispositivoMinutos: -agora.getTimezoneOffset(),
      ...(valores.actorUserId ? { actorUserId: valores.actorUserId } : {}),
      ...(valores.celulaCodigo ? { celulaCodigo: valores.celulaCodigo } : {}),
    },
  };
}

/** Linha de apoio da tela de concluir: o que chegou (quando houver), o pendente e o alvo (D-12). */
export function descricaoConcluir(cartao: {
  itemNome: string;
  quantidadePendente: number;
  quantidadeAlvo: number;
  quantidadeDisponivel?: number;
}): string {
  const chegou = (cartao.quantidadeDisponivel ?? 0) > 0 ? `chegou ${cartao.quantidadeDisponivel} · ` : '';
  return `${cartao.itemNome} · ${chegou}pendente ${cartao.quantidadePendente} de ${cartao.quantidadeAlvo}`;
}

export type DecisaoConfirmacao =
  | { tipo: 'enviar'; valores: ValoresConclusao }
  | { tipo: 'erro'; mensagem: string }
  | { tipo: 'confirmar-excesso'; mensagem: string };

/**
 * Decide o que um toque em "Concluir/Registrar" faz: envia, mostra erro de validação ou pede a segunda
 * confirmação quando o total passa do pendente (WR-WEB-07). Com os valores pré-preenchidos, 1 toque envia.
 */
export function avaliarConfirmacao(entrada: {
  boaTexto: string;
  ncTexto: string;
  motivo: string;
  quantidadePendente: number;
  confirmarExcesso: boolean;
}): DecisaoConfirmacao {
  const valores: ValoresConclusao = {
    quantidadeBoa: converterQuantidade(entrada.boaTexto),
    quantidadeNaoConforme: converterQuantidade(entrada.ncTexto),
    motivoNc: entrada.motivo,
  };
  const mensagem = validarConclusao({
    boa: valores.quantidadeBoa,
    nc: valores.quantidadeNaoConforme,
    motivo: entrada.motivo,
  });
  if (mensagem) return { tipo: 'erro', mensagem };
  if (
    !entrada.confirmarExcesso &&
    excedePendente({ boa: valores.quantidadeBoa, nc: valores.quantidadeNaoConforme }, entrada.quantidadePendente)
  ) {
    const total = valores.quantidadeBoa + valores.quantidadeNaoConforme;
    return {
      tipo: 'confirmar-excesso',
      mensagem: `O total (${total}) é maior que o pendente (${entrada.quantidadePendente}). Toque de novo para confirmar.`,
    };
  }
  return { tipo: 'enviar', valores };
}
